import React, { useLayoutEffect, useRef, useState } from "react";

interface MiddleTruncateTextProps {
  text: string;
  maxWidth: number;
  style?: React.CSSProperties;
}

const MiddleTruncateTextPx: React.FC<MiddleTruncateTextProps> = ({
  text,
  maxWidth,
  style = {},
}) => {
  const [displayText, setDisplayText] = useState<string>(text);
  const measureRef = useRef<HTMLSpanElement | null>(null);
  const ellipsis = "[...]";

  interface PrevDeps {
    text: string;
    maxWidth: number;
    font: string;
  }

  const prevDepsRef = useRef<PrevDeps>({ text: "", maxWidth: 0, font: "" });

  useLayoutEffect(() => {
    if (!measureRef.current) return;

    const font = style.font || "";
    const depsChanged =
      text !== prevDepsRef.current.text ||
      maxWidth !== prevDepsRef.current.maxWidth ||
      font !== prevDepsRef.current.font;

    if (!depsChanged) return;

    prevDepsRef.current = { text, maxWidth, font };

    const measureWidth = (str: string): number => {
      if (measureRef.current) {
        measureRef.current.textContent = str;

        return measureRef.current.getBoundingClientRect().width;
      }

      return 0;
    };

    if (measureWidth(text) <= maxWidth) {
      setDisplayText((prev) => (prev !== text ? text : prev));

      return;
    }

    let low = 0;
    let high = text.length;
    let best = ellipsis;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const leftChars = Math.ceil(mid / 2);
      const rightChars = Math.floor(mid / 2);

      const prefix = text.slice(0, leftChars);
      const suffix = text.slice(text.length - rightChars);
      const candidate = `${prefix}${ellipsis}${suffix}`;
      const width = measureWidth(candidate);

      if (width <= maxWidth) {
        best = candidate;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    setDisplayText((prev) => (prev !== best ? best : prev));
  }, [text, maxWidth, style.font]);

  return (
    <>
      {/* Hidden span used for accurate measurement */}
      <span
        ref={measureRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          ...style,
        }}
      />
      <span>{displayText}</span>
    </>
  );
};

export default MiddleTruncateTextPx;
